<template>
  <div class="elpako-signature-step">
    <!-- Step header -->
    <div class="signature-step-header mb-4">
      <h4 class="step-title">
        <i class="fas fa-pen-alt"></i>
        <span class="ml-4">Elektroninis parašas</span>
      </h4>
      <p class="step-description text-muted">
        Jūsų prašymas turi būti pasirašytas elektroniniu parašu prieš pateikimą.
      </p>
    </div>

    <!-- Signature status display -->
    <div class="card signature-status-card" :class="statusCardClass">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-auto">
            <div class="signature-status-icon">
              <i :class="statusIcon" class="fa-xl"></i>
            </div>
          </div>
          <div class="col">
            <h5 class="signature-status-title mb-1">
              {{ statusTitle }}
            </h5>
            <p class="signature-status-message mb-0">
              {{ statusMessage }}
            </p>
            <small
              v-if="signatureData && signatureData.signed_at"
              class="text-muted"
            >
              Pasirašyta: {{ formatDate(signatureData.signed_at) }}
            </small>
            <small v-if="reference" class="text-muted d-block">
              Nuorodos kodas: {{ reference }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Document preview -->
    <div v-if="documentUrl" class="document-preview-section mt-6">
      <h6 class="mb-4">Dokumento peržiūra</h6>
      <div class="document-preview-card">
        <div class="card-body text-center">
          <i class="fas fa-file-pdf fa-3x text-danger mb-3"></i>
          <!--          <p class="mb-2">{{ documentName }}</p>-->
          <div>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="loading"
              @click="previewDocument"
            >
              <i class="fas fa-eye"></i> Peržiūrėti dokumentą
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Action buttons -->
    <div class="signature-actions mt-4">
      <template v-if="status === 'pending_signature'">
        <button
          type="button"
          class="btn btn-primary blue-fill-in me-3"
          :disabled="loading"
          @click="startSigning"
        >
          <template v-if="loading">
            <i class="fas fa-spinner fa-spin"></i>
            Prašome palaukti...
          </template>
          <template v-else>
            <i class="fas fa-pen-alt"></i>
            Pasirašyti dokumentą
          </template>
        </button>

        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="refreshStatus"
        >
          <i class="fas fa-sync-alt"></i>
          Atnaujinti būseną
        </button>
      </template>

      <template v-if="status === 'signing_in_progress'">
        <button
          type="button"
          class="btn btn-warning btn-lg me-3"
          :disabled="loading"
          @click="checkSigningProgress"
        >
          <i class="fas fa-clock"></i>
          Tikrinti progresą
        </button>

        <button
          type="button"
          class="btn btn-outline-danger"
          :disabled="loading"
          @click="cancelSigning"
        >
          <i class="fas fa-times"></i>
          Atšaukti pasirašymą
        </button>
      </template>

      <template v-if="status === 'signed'">
        <button
          type="button"
          class="btn greene-light-fill-in me-3"
          :disabled="loading"
          @click="downloadSignedDocument"
        >
          <i class="fas fa-download"></i>
          Atsisiųsti pasirašytą dokumentą
        </button>

        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="verifySignature"
        >
          <i class="fas fa-shield-alt"></i>
          Patikrinti parašą
        </button>
      </template>
    </div>

    <!-- Error display -->
    <div v-if="error" class="alert alert-danger mt-4" role="alert">
      <h6 class="alert-heading">
        <i class="fas fa-exclamation-triangle"></i>
        Klaida
      </h6>
      <p class="mb-0">{{ error }}</p>
      <ul v-if="errorDetails.length" class="mt-2 mb-0 small">
        <li v-for="(detail, idx) in errorDetails" :key="idx">
          <strong>Kodas:</strong> {{ detail.error_code || '—' }} –
          {{ detail.message || 'Nežinoma klaida' }}
        </li>
      </ul>
      <hr />
      <button
        type="button"
        class="btn btn-outline-danger btn-sm"
        :disabled="loading"
        @click="retryLastAction"
      >
        <i class="fas fa-redo"></i>
        Bandyti dar kartą
      </button>
    </div>

    <!-- Success messages -->
    <div v-if="successMessage" class="alert alert-success mt-4" role="alert">
      <i class="fas fa-check-circle"></i>
      {{ successMessage }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ElpakoSignature',
  props: {
    tenderId: {
      type: [Number, String],
      required: true,
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
    initialStatus: {
      type: String,
      default: 'pending_signature',
    },
  },
  emits: ['signature-completed', 'signature-error', 'status-changed'],
  data() {
    return {
      loading: false,
      status: this.initialStatus, // pending_signature, signing_in_progress, signed, error
      signatureData: null,
      sessionId: null,
      documentUrl: null,
      documentName: null,
      error: null,
      successMessage: null,
      lastAction: null,
      pollingInterval: null,
      errorDetails: [],
      elpakoStatus: null,
      reference: null,
    }
  },
  computed: {
    statusCardClass() {
      return {
        'border-warning': this.status === 'pending_signature',
        'border-info': this.status === 'signing_in_progress',
        'border-success': this.status === 'signed',
        'border-danger': this.status === 'error' || this.status === 'cancelled',
      }
    },
    statusIcon() {
      switch (this.status) {
        case 'pending_signature':
          return 'fas fa-clock text-warning'
        case 'signing_in_progress':
          return 'fas fa-spinner fa-spin text-info'
        case 'signed':
          return 'fas fa-check-circle text-success'
        case 'cancelled':
          return 'fas fa-ban text-danger'
        case 'error':
          return 'fas fa-exclamation-triangle text-danger'
        default:
          return 'fas fa-question-circle text-muted'
      }
    },
    statusTitle() {
      switch (this.status) {
        case 'pending_signature':
          return 'Laukiama parašo'
        case 'signing_in_progress':
          return 'Vyksta pasirašymas'
        case 'signed':
          return 'Dokumentas pasirašytas'
        case 'cancelled':
          return 'Pasirašymas atšauktas'
        case 'error':
          return 'Klaida'
        default:
          return 'Nežinoma būsena'
      }
    },
    statusMessage() {
      switch (this.status) {
        case 'pending_signature':
          return 'Dokumentas paruoštas pasirašymui. Paspauskite mygtuką "Pasirašyti dokumentą" kad pradėtumėte procesą.'
        case 'signing_in_progress':
          return 'Dokumentas siunčiamas į Elpako sistemą. Neuždarykite šio lango.'
        case 'signed':
          return 'Dokumentas sėkmingai pasirašytas elektroniniu parašu. Galite tęsti prašymo pateikimą.'
        case 'cancelled':
          return 'Pasirašymo procesas buvo atšauktas. Jei norite, galite pradėti pasirašymą iš naujo.'
        case 'error':
          return 'Įvyko klaida pasirašymo proceso metu. Pabandykite dar kartą.'
        default:
          return ''
      }
    },
  },
  created() {
    this.initializeSignature()
  },
  beforeUnmount() {
    this.clearPolling()
  },
  methods: {
    async initializeSignature() {
      try {
        this.loading = true
        this.clearError()

        const response = await axios.get(
          `/account/tenders/${this.tenderId}/signature/status`,
        )

        if (response.data.status === 'success') {
          this.updateSignatureData(response.data.data)
        }
      } catch (error) {
        this.handleError('Nepavyko gauti parašo informacijos', error)
      } finally {
        this.loading = false
      }
    },

    async startSigning() {
      try {
        this.loading = true
        this.clearError()
        this.lastAction = 'startSigning'

        const response = await axios.post(
          `/account/tenders/${this.tenderId}/signature/start`,
          {
            form_data: this.formData,
          },
        )

        if (response.data.status !== 'success') {
          throw new Error(
            response.data.message || 'Nepavyko pradėti parašymo proceso',
          )
        }

        const data = response.data.data
        this.sessionId = data.session_id
        this.documentUrl = data.document_url
        this.documentName = data.document_name
        this.reference = data.reference || null
        this.status = data.signature_status || 'signing_in_progress'
        this.errorDetails = []
        this.elpakoStatus = null

        const signingLink = data.signing_url || data.redirect_url
        if (signingLink) {
          window.open(
            signingLink,
            '_blank',
            'width=800,height=600,scrollbars=yes,resizable=yes',
          )
          this.startPolling()
        }

        this.$emit('status-changed', this.status)
        this.showSuccess('Parašymo procesas pradėtas')
      } catch (error) {
        this.handleError('Nepavyko pradėti parašymo proceso', error)
      } finally {
        this.loading = false
      }
    },

    async checkSigningProgress() {
      if (!this.sessionId) return

      try {
        this.loading = true
        this.clearError()

        const response = await axios.get(
          `/account/tenders/${this.tenderId}/signature/status/${this.sessionId}`,
        )

        if (response.data.status === 'success') {
          this.updateSignatureData(response.data.data)
        }
      } catch (error) {
        this.handleError('Nepavyko patikrinti parašymo progreso', error)
      } finally {
        this.loading = false
      }
    },

    async cancelSigning() {
      if (!this.sessionId) return

      if (!confirm('Ar tikrai norite atšaukti parašymo procesą?')) {
        return
      }

      try {
        this.loading = true
        this.clearError()

        const response = await axios.delete(
          `/account/tenders/${this.tenderId}/signature/cancel/${this.sessionId}`,
        )

        if (response.data.status === 'success') {
          this.status = 'pending_signature'
          this.sessionId = null
          this.clearPolling()
          this.$emit('status-changed', this.status)
          this.showSuccess('Parašymo procesas atšauktas')
        }
      } catch (error) {
        this.handleError('Nepavyko atšaukti parašymo proceso', error)
      } finally {
        this.loading = false
      }
    },

    async downloadSignedDocument() {
      if (!this.sessionId) return

      try {
        this.loading = true
        this.clearError()

        const response = await axios.get(
          `/account/tenders/${this.tenderId}/signature/download/${this.sessionId}`,
          {
            responseType: 'blob',
          },
        )

        // Create download link
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download =
          this.documentName || `tender_${this.tenderId}_signed.pdf`
        link.click()
        window.URL.revokeObjectURL(url)

        this.showSuccess('Dokumentas atsisiųstas')
      } catch (error) {
        this.handleError('Nepavyko atsisiųsti dokumento', error)
      } finally {
        this.loading = false
      }
    },

    async verifySignature() {
      if (!this.sessionId) return

      try {
        this.loading = true
        this.clearError()

        const response = await axios.get(
          `/account/tenders/${this.tenderId}/signature/verify/${this.sessionId}`,
        )

        if (response.data.status === 'success') {
          const verification = response.data.data
          let message = 'Parašas patikrintas: '
          message += verification.valid
            ? 'Parašas galiojantis ✓'
            : 'Parašas negaliojantis ✗'
          this.showSuccess(message)
        }
      } catch (error) {
        this.handleError('Nepavyko patikrinti parašo', error)
      } finally {
        this.loading = false
      }
    },

    async refreshStatus() {
      await this.checkSigningProgress()
    },

    previewDocument() {
      // Always use backend preview endpoint so we do not leak Elpako access tokens
      if (!this.tenderId) return
      window.open(
        `/account/tenders/${this.tenderId}/signature/preview`,
        '_blank',
      )
    },

    retryLastAction() {
      if (this.lastAction && typeof this[this.lastAction] === 'function') {
        this[this.lastAction]()
      }
    },

    startPolling() {
      this.clearPolling()
      this.pollingInterval = setInterval(() => {
        this.checkSigningProgress()
      }, 5000) // Check every 5 seconds
    },

    clearPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    updateSignatureData(data) {
      this.signatureData = data
      this.sessionId = data.session_id
      this.documentUrl = data.document_url
      this.documentName = data.document_name
      this.reference = data.reference || null
      this.elpakoStatus = data.elpako_status || null
      this.errorDetails = Array.isArray(data.errors) ? data.errors : []

      if (this.errorDetails.length && data.error_message) {
        this.error = data.error_message
      } else if (!this.errorDetails.length) {
        this.error = null
      }

      if (data.signature_status) {
        const oldStatus = this.status
        this.status = data.signature_status

        if (oldStatus !== this.status) {
          this.$emit('status-changed', this.status)
        }

        if (this.status === 'signed') {
          this.clearPolling()
          this.$emit('signature-completed', data)
          this.showSuccess('Dokumentas sėkmingai pasirašytas!')
        }
      }
    },

    handleError(message, error) {
      console.error('Signature error:', error)
      this.error = message
      const vendorErrors =
        error?.response?.data?.data?.errors || error?.response?.data?.errors
      if (Array.isArray(vendorErrors)) {
        this.errorDetails = vendorErrors
      }
      this.status = 'error'
      this.$emit('signature-error', { message, error })
      this.clearPolling()
    },

    clearError() {
      this.error = null
      this.successMessage = null
      this.errorDetails = []
    },

    showSuccess(message) {
      this.successMessage = message
      setTimeout(() => {
        this.successMessage = null
      }, 5000)
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleString('lt-LT', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.signature-step-header {
  text-align: center;
  padding: 0 0 1rem;
}

.step-title {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.step-description {
  font-size: 1.1rem;
}

.signature-status-card {
  border-width: 2px;
  /*box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);*/
}

.signature-status-icon {
  width: 60px;
  text-align: center;
}

.document-preview-card {
  border: 1px dashed #dee2e6;
  background-color: #ffffff;
}

.signature-actions {
  text-align: center;
  padding: 1rem 0 1rem;
}

.signature-actions .btn {
  margin: 0.25rem;
}

.alert {
  border-radius: 0.5rem;
}

@media (max-width: 768px) {
  .signature-actions .btn {
    display: block;
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .signature-actions .me-3 {
    margin-right: 0 !important;
  }
  .signature-status-icon {
    display: none;
  }
}
</style>
